import { useCallback, useEffect, useRef } from 'react';
import type {
  AnalyticsDialogsRequest,
  AnalyticsTopic,
  GetQuestionsStatsAnalyticsStatsQuestionsPostApiArg,
} from '@shared/api';
import { useGetQuestionsStatsAnalyticsStatsQuestionsPostMutation } from '@shared/api';

type AnalyticsQuestionsResult = {
  data?: AnalyticsTopic[];
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  refetch: () => void;
  reset: () => void;
};

export const useQuestionsStats = (
  params: AnalyticsDialogsRequest | null,
  enabled = true,
): AnalyticsQuestionsResult => {
  const [trigger, result] = useGetQuestionsStatsAnalyticsStatsQuestionsPostMutation();
  const lastArgsRef = useRef<GetQuestionsStatsAnalyticsStatsQuestionsPostApiArg | null>(null);

  const fetch = useCallback(
    async (p: AnalyticsDialogsRequest) => {
      const arg: GetQuestionsStatsAnalyticsStatsQuestionsPostApiArg = {
        analyticsDialogsRequest: p,
      };
      lastArgsRef.current = arg;
      try {
        await trigger(arg).unwrap();
      } catch {}
    },
    [trigger],
  );

  const refetch = useCallback(() => {
    if (!lastArgsRef.current) return;
    void trigger(lastArgsRef.current)
      .unwrap()
      .catch(() => {});
  }, [trigger]);

  useEffect(() => {
    if (!enabled || !params) return;
    void fetch(params);
  }, [enabled, params, fetch]);

  return {
    data: result.data,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
    refetch,
    reset: result.reset,
  };
};
